import React, { useState, useCallback, useRef, useEffect } from 'react';
import Card from '~/src/components/Card';
import Button from '~/src/components/Button';
import InlineNotification from '~/src/components/Notification/InlineNotification';
import ModalOverlay from '~/src/components/Modal/Overlay';
import QuantityControl from '~/src/components/QuantityControl/QuantityControl';
import cbsSubscriptionsService from '~/src/services/api/cbsSubscriptionsService';
import useUser from '~/src/hooks/useUser';
import { LicenseQuantityPreviewModal } from './LicenseQuantityPreviewModal';
import { useCbsSubscriptionDetails } from '~/src/hooks/cbs/useCbsSubscriptionDetails';

interface LicenseQuantityCardProps {
  subscription: any;
}

export const LicenseQuantityCard = ({
  subscription,
}: LicenseQuantityCardProps) => {
  const [loading, setLoading] = useState(false);
  const [previewData, setPreviewData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedQuantity, setSelectedQuantity] = useState(
    subscription?.paidQuantity || 1,
  );
  const [isProcessing, setIsProcessing] = useState(false);
  const { currentOrganization } = useUser();
  const isMountedRef = useRef(true);

  const memberCount = currentOrganization?.members?.length || 1;

  const {
    data: cbsDetails,
    isLoading: detailsLoading,
    error: cbsDetailsError,
  } = useCbsSubscriptionDetails();

  const hasPendingLicenseChange =
    cbsDetails?.has_pending_license_quantity_change || false;

  const hasCbsError = Boolean(cbsDetailsError);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const currentQuantity = subscription?.paidQuantity || 1;

  const handleIncrement = () => {
    setSelectedQuantity((prev: number) => prev + 1);
  };

  const handleDecrement = () => {
    if (selectedQuantity > memberCount) {
      setSelectedQuantity((prev: number) => prev - 1);
    }
  };

  const handleQuantityChange = useCallback(
    async (newQuantity: number) => {
      if (newQuantity === currentQuantity) return;

      setLoading(true);
      setError(null);
      setPreviewData(null);

      try {
        const orgFprint = currentOrganization?.fprint;
        if (!orgFprint) {
          throw new Error('Organization fingerprint not found');
        }

        const params = {
          license_qty: newQuantity.toString(),
        };

        const result = await cbsSubscriptionsService.getSubscriptionPreview(
          orgFprint,
          params,
        );

        if (!isMountedRef.current) return;

        if (
          result &&
          (result.preview || result.data || result.recurring_invoice)
        ) {
          setPreviewData(result);
          setShowModal(true);
        } else {
          setError('Invalid preview data received from server');
        }
      } catch (err: any) {
        if (!isMountedRef.current) return;
        setError(err.message || 'An error occurred while generating preview');
      } finally {
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    },
    [currentOrganization?.fprint, currentQuantity],
  );

  const handleConfirmChange = useCallback(async () => {
    if (!currentOrganization?.fprint) {
      setError('Missing required information for license quantity update');
      return;
    }

    setIsProcessing(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const updateData = {
        license_qty: selectedQuantity,
      };

      await cbsSubscriptionsService.updateSubscription(
        currentOrganization.fprint,
        updateData,
      );

      if (!isMountedRef.current) return;

      setShowModal(false);
      setPreviewData(null);

      // Show success message
      setSuccessMessage(
        `Successfully updated to ${selectedQuantity} license${
          selectedQuantity === 1 ? '' : 's'
        }!`,
      );

      // Reload after showing success message briefly
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (err: any) {
      if (!isMountedRef.current) return;
      setShowModal(false);
      setPreviewData(null);
      setError(err.message || 'Failed to update license quantity');
    } finally {
      if (isMountedRef.current) {
        setIsProcessing(false);
      }
    }
  }, [currentOrganization?.fprint, selectedQuantity]);

  const handleCancelPreview = () => {
    setShowModal(false);
    setPreviewData(null);
    setError(null);
    // Reset selected quantity back to current quantity
    setSelectedQuantity(currentQuantity);
  };

  const hasChanges = selectedQuantity !== currentQuantity;
  // On any failure we hide the update affordance and leave only the error
  // message — the user should start the change over rather than re-submit.
  const hasError = Boolean(error);

  const LicenseQuantityHeader = () => (
    <div className="flex items-center justify-between">
      <div className="text-lg font-bold">Change License Quantity</div>
      <QuantityControl
        value={selectedQuantity}
        onIncrement={handleIncrement}
        onDecrement={handleDecrement}
        disabled={loading}
        minValue={memberCount}
      />
    </div>
  );

  const CardPendingLicenseChangeContent = () => (
    <div className="mt-3">
      <InlineNotification
        type="warning"
        title="Pending License Quantity Change"
        message="Your license quantity is scheduled to change. For assistance, please contact Clio Support."
      />
    </div>
  );

  if (detailsLoading) {
    return (
      <Card>
        <LicenseQuantityHeader />
      </Card>
    );
  } else if (hasPendingLicenseChange) {
    return (
      <Card>
        <LicenseQuantityHeader />
        <CardPendingLicenseChangeContent />
      </Card>
    );
  } else {
    return (
      <>
        <Card>
          <LicenseQuantityHeader />

          <div className="space-y-4 mt-0.5">
            {hasChanges && !hasError && (
              <>
                {/* License change warning message */}
                <div
                  className="bg-yellow-50 border border-yellow-300 text-yellow-800 rounded px-3 py-2 text-sm mt-4 mb-2"
                  role="alert"
                  aria-label="License Change Warning"
                >
                  {selectedQuantity > currentQuantity
                    ? 'Changes to your license count will be reflected on your Clio subscription and billed with immediate effect.'
                    : 'Changes to your license count will be reflected on your Clio subscription and billed at the end of your current billing cycle.'}
                </div>
                <div className="pt-2">
                  <Button
                    onClick={() => handleQuantityChange(selectedQuantity)}
                    disabled={loading}
                    primary
                    className="w-full py-2 text-sm"
                  >
                    {loading
                      ? 'Loading preview...'
                      : `Update to ${selectedQuantity} license${
                          selectedQuantity === 1 ? '' : 's'
                        }`}
                  </Button>
                </div>
              </>
            )}

            {selectedQuantity <= memberCount && memberCount > 1 && (
              <InlineNotification
                type="warning"
                title="Minimum licenses required"
                message={`You have ${memberCount} active user${
                  memberCount === 1 ? '' : 's'
                } and cannot reduce licenses below ${memberCount}.`}
              />
            )}

            {successMessage && (
              <InlineNotification
                type="success"
                title="Success"
                message={successMessage}
              />
            )}

            {(hasCbsError || error) && (
              <InlineNotification
                type="error"
                title="Service Unavailable"
                message={error || 'Please try again later.'}
              />
            )}
          </div>
        </Card>

        <ModalOverlay show={showModal} onCancel={handleCancelPreview}>
          {previewData && (
            <LicenseQuantityPreviewModal
              previewData={previewData}
              newLicenseQuantity={selectedQuantity}
              currentLicenseQuantity={currentQuantity}
              onCancel={handleCancelPreview}
              onConfirm={handleConfirmChange}
              isProcessing={isProcessing}
            />
          )}
        </ModalOverlay>
      </>
    );
  }
};
